import { ChevronDown, Search } from "lucide-react";
import * as React from "react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface DepartmentFilterProps {
  value: string[];
  onChange: (value: string[]) => void;
  departments: string[];
  placeholder?: string;
  className?: string;
}

export const DepartmentFilter: React.FC<DepartmentFilterProps> = ({
  value,
  onChange,
  departments,
  placeholder = "All Departments",
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const selectedValues = React.useMemo(() => value ?? [], [value]);

  const filteredDepartments = React.useMemo(() => {
    if (!searchTerm.trim()) return departments;
    return departments.filter((department) =>
      department.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [departments, searchTerm]);

  const handleValueToggle = (department: string) => {
    if (department === "") {
      onChange([]);
      return;
    }

    const nextValue = selectedValues.includes(department)
      ? selectedValues.filter((item) => item !== department)
      : [...selectedValues, department];

    onChange(nextValue);
  };

  const selectedLabels = selectedValues.join(", ");

  return (
    <div className="flex items-center gap-2 w-full min-w-[180px] md:w-[240px]">
      <DropdownMenu
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setSearchTerm("");
        }}
        modal={false}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "relative flex items-center w-full bg-[#F5F6F7] dark:bg-[#0c3451] rounded-xl px-4 py-1.5 font-bold text-primary dark:text-white text-sm focus:outline-none border-0 shadow-none h-[36px] hover:bg-[#e9eaeb] dark:hover:bg-[#2C4C63] transition justify-between",
              className
            )}
            style={{ boxShadow: "none" }}
          >
            <span className="truncate text-left text-sm w-full pr-6 text-primary dark:text-white">
              {selectedLabels || placeholder}
            </span>
            <ChevronDown className="w-4 h-4 text-primary dark:text-white absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[320px] p-0 text-sm bg-white dark:bg-[#3C5C73] max-h-[300px] overflow-hidden"
          align="start"
        >
          <div className="p-3 border-b border-gray-200 dark:border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-[200px] overflow-y-auto">
            <div className="p-2">
              <DropdownMenuCheckboxItem
                checked={selectedValues.length === 0}
                onCheckedChange={() => handleValueToggle("")}
                onSelect={(e) => e.preventDefault()}
                className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer"
              >
                All Departments
              </DropdownMenuCheckboxItem>

              {filteredDepartments.length === 0 && searchTerm.trim() ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No departments found
                </div>
              ) : (
                filteredDepartments.map((department) => (
                  <DropdownMenuCheckboxItem
                    key={department}
                    checked={selectedValues.includes(department)}
                    onCheckedChange={() => handleValueToggle(department)}
                    onSelect={(e) => e.preventDefault()}
                    className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer"
                  >
                    {department}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
